import { fromPairs, isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { defaultApi, useGetWorkflowStepConfigSettingFieldsQuery } from '@/middlewares/defaultApi';
import type { RootState } from '@/reducers/reducers';
import { selectIsAppsDemoModeEnabled, selectStepConfig } from '@/selectors/selectors';
import type { SettingField } from '@/types';
import { demoStepConfigSettingFields } from '@/testData/stepConfigSettingFields';

export const INTEGRATIONS_SETTINGS_GROUP = 'integrations';

const useRefetchWhenLoadedValuesChange = (values: Record<string, unknown>, skip: boolean, refetch: () => void) => {
    const previousValues = useRef(values);
    useEffect(() => {
        const hasLoadedValueChanged = Object.entries(values).some(
            ([key, value]) => previousValues.current[key] !== undefined && !isEqual(previousValues.current[key], value),
        );
        previousValues.current = values;
        if (hasLoadedValueChanged && !skip) {
            refetch();
        }
    });
};

export const useStepConfigSettingFields = ({
    orgId,
    workflowId,
    stepConfigId,
    skip = false,
}: {
    orgId: string;
    workflowId: string | undefined;
    stepConfigId?: string;
    skip?: boolean;
}): { settingFields: SettingField[] | undefined; isSettingFieldsError: boolean } => {
    const isDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const stepConfig = useSelector((state: RootState) =>
        stepConfigId ? selectStepConfig(state, stepConfigId) : undefined,
    );
    const workflowSettingFields = defaultApi.endpoints.getWorkflowStepConfigSettingFields.useQueryState(
        workflowId ? { orgId, workflowId } : skipToken,
    );
    const workflowSettingFieldsForStep =
        stepConfigId ? workflowSettingFields.currentData?.data?.[stepConfigId] : undefined;
    const isStepInWorkflowSettingFields =
        !!stepConfigId && (workflowSettingFieldsForStep !== undefined || workflowSettingFields.isLoading);
    const shouldSkip = skip || isDemoMode || !workflowId || isStepInWorkflowSettingFields;
    const { currentData, isError, refetch } = useGetWorkflowStepConfigSettingFieldsQuery(
        stepConfigId ? { orgId, workflowId: workflowId ?? '', stepConfigId } : { orgId, workflowId: workflowId ?? '' },
        { skip: shouldSkip, refetchOnMountOrArgChange: false, refetchOnFocus: false },
    );

    const loadedStepConfigIds = Object.keys(currentData?.data ?? {});
    const stepConfigUpdatedAtById = useSelector(
        (state: RootState) =>
            fromPairs(
                loadedStepConfigIds.map((loadedStepConfigId) => [
                    loadedStepConfigId,
                    selectStepConfig(state, loadedStepConfigId)?.updatedAt,
                ]),
            ),
        isEqual,
    );
    useRefetchWhenLoadedValuesChange(stepConfigUpdatedAtById, shouldSkip, refetch);

    if (isDemoMode) {
        return {
            settingFields: stepConfig ? demoStepConfigSettingFields(stepConfig) : undefined,
            isSettingFieldsError: false,
        };
    }
    return {
        settingFields: workflowSettingFieldsForStep ?? (stepConfigId ? currentData?.data?.[stepConfigId] : undefined),
        isSettingFieldsError: isError,
    };
};
